import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import HRLayout from "@/components/HRLayout";
import Login from "@/pages/Login";
import PortalLogin from "@/pages/PortalLogin";
import ModuleHub from "@/pages/ModuleHub";
import PurchasesHub from "@/pages/PurchasesHub";
import PriceTags from "@/pages/PriceTags";
import PurchasesStore from "@/pages/PurchasesStore";
import PurchasesMappings from "@/pages/PurchasesMappings";
import Dashboard from "@/pages/Dashboard";
import Journal from "@/pages/Journal";
import Corrections from "@/pages/Corrections";
import Reports from "@/pages/Reports";
import Documents from "@/pages/Documents";
import Locations from "@/pages/Locations";
import ZTicket from "@/pages/ZTicket";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import HRHome from "@/pages/hr/HRHome";
import KassaApp from "@/pages/kassa/KassaApp";
import CustomerDisplay from "@/pages/kassa/CustomerDisplay";
import HREmployeeDossier from "@/pages/hr/HREmployeeDossier";
import HRUpload from "@/pages/hr/HRUpload";
import HRReports from "@/pages/hr/HRReports";
import HRExport from "@/pages/hr/HRExport";
import HRTrash from "@/pages/hr/HRTrash";
import ZTicketInbox from "@/pages/ZTicketInbox";
import BoekhoudingHub from "@/pages/BoekhoudingHub";
import Kasboek from "@/pages/Kasboek";
import Factuurboek from "@/pages/Factuurboek";
import FactuurboekLeveranciers from "@/pages/FactuurboekLeveranciers";
import FactuurboekLeveranciersrekening from "@/pages/FactuurboekLeveranciersrekening";
import FactuurboekOverzicht from "@/pages/FactuurboekOverzicht";
import FactuurboekOpbrengsten from "@/pages/FactuurboekOpbrengsten";
import FactuurboekAutokosten from "@/pages/FactuurboekAutokosten";
import FactuurboekEnergie from "@/pages/FactuurboekEnergie";
import BankProviders from "@/pages/BankProviders";
import Grootboek from "@/pages/Grootboek";
import VasteActiva from "@/pages/VasteActiva";
import RapportenHub from "@/pages/RapportenHub";
import RapportenOpbrengsten from "@/pages/RapportenOpbrengsten";
import RapportenMarge from "@/pages/RapportenMarge";
import Resultaten from "@/pages/Resultaten";
import { singhRoutes } from "./singh/routes";

function useAuthGuard() {
  const { user, ready } = useAuth();
  if (!ready) return { loading: true };
  if (!user) return { redirect: true };
  return { ok: true };
}

function Protected({ children }) {
  const g = useAuthGuard();
  if (g.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Laden…</div>;
  if (g.redirect) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

const singhAfmelden = () => {
  sessionStorage.removeItem("singh_dagontvangsten_ok");
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kassa/auth/logout`, { method: "POST", credentials: "include" })
    .finally(() => { window.location.href = "/"; });
};

function ProtectedHR({ children }) {
  const g = useAuthGuard();
  if (g.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Laden…</div>;
  if (g.redirect) return <Navigate to="/" replace />;
  return <HRLayout>{children}</HRLayout>;
}

function ProtectedKasboek({ children }) {
  const g = useAuthGuard();
  if (g.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Laden…</div>;
  if (g.redirect) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedFactuur({ children }) {
  const g = useAuthGuard();
  if (g.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Laden…</div>;
  if (g.redirect) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedPlain({ children }) {
  const g = useAuthGuard();
  if (g.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Laden…</div>;
  if (g.redirect) return <Navigate to="/" replace />;
  return children;
}

function ProtectedRapporten({ children }) {
  const g = useAuthGuard();
  if (g.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Laden…</div>;
  if (g.redirect) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PortalLogin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/hub" element={<ProtectedPlain><ModuleHub /></ProtectedPlain>} />
            <Route path="/purchases" element={<ProtectedPlain><PurchasesHub /></ProtectedPlain>} />
            <Route path="/price-tags" element={<ProtectedPlain><PriceTags /></ProtectedPlain>} />
            <Route path="/purchases/koppelingen" element={<ProtectedPlain><PurchasesMappings /></ProtectedPlain>} />
            <Route path="/purchases/:code" element={<ProtectedPlain><PurchasesStore /></ProtectedPlain>} />
            <Route path="/boekhouding" element={<ProtectedPlain><BoekhoudingHub /></ProtectedPlain>} />
            <Route path="/rapporten" element={<ProtectedPlain><RapportenHub /></ProtectedPlain>} />
            <Route path="/rapporten/opbrengsten" element={<ProtectedRapporten><RapportenOpbrengsten /></ProtectedRapporten>} />
            <Route path="/rapporten/diverse" element={<ProtectedRapporten><FactuurboekOpbrengsten /></ProtectedRapporten>} />
            <Route path="/rapporten/marge" element={<ProtectedRapporten><RapportenMarge /></ProtectedRapporten>} />            <Route path="/rapporten/overzicht" element={<ProtectedRapporten><FactuurboekOverzicht /></ProtectedRapporten>} />
            <Route path="/rapporten/autokosten" element={<ProtectedRapporten><FactuurboekAutokosten /></ProtectedRapporten>} />
            <Route path="/rapporten/energie" element={<ProtectedRapporten><FactuurboekEnergie /></ProtectedRapporten>} />            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/kasboek" element={<ProtectedKasboek><Kasboek /></ProtectedKasboek>} />
            <Route path="/factuurboek" element={<ProtectedFactuur><Factuurboek /></ProtectedFactuur>} />
            <Route path="/providers" element={<ProtectedFactuur><BankProviders /></ProtectedFactuur>} />
            <Route path="/grootboek" element={<ProtectedFactuur><Grootboek /></ProtectedFactuur>} />
            <Route path="/vaste-activa" element={<ProtectedFactuur><VasteActiva /></ProtectedFactuur>} />
            <Route path="/factuurboek/leveranciers" element={<ProtectedFactuur><FactuurboekLeveranciers /></ProtectedFactuur>} />
            <Route path="/factuurboek/leveranciersrekening" element={<ProtectedFactuur><FactuurboekLeveranciersrekening /></ProtectedFactuur>} />
            <Route path="/factuurboek/overzicht" element={<ProtectedFactuur><FactuurboekOverzicht /></ProtectedFactuur>} />
            <Route path="/factuurboek/opbrengsten" element={<ProtectedFactuur><FactuurboekOpbrengsten /></ProtectedFactuur>} />
            <Route path="/factuurboek/autokosten" element={<ProtectedFactuur><FactuurboekAutokosten /></ProtectedFactuur>} />
            <Route path="/factuurboek/energie" element={<ProtectedFactuur><FactuurboekEnergie /></ProtectedFactuur>} />
            <Route path="/journal" element={<Protected><Journal /></Protected>} />
            <Route path="/z-ticket" element={<Protected><ZTicket /></Protected>} />
            <Route path="/z-inbox" element={<ProtectedPlain><ZTicketInbox /></ProtectedPlain>} />
            <Route path="/corrections" element={<Protected><Corrections /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/locations" element={<Protected><Locations /></Protected>} />
            <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/hr" element={<ProtectedHR><HRHome /></ProtectedHR>} />
            <Route path="/hr/employee/:id" element={<ProtectedHR><HREmployeeDossier /></ProtectedHR>} />
            <Route path="/hr/upload" element={<ProtectedHR><HRUpload /></ProtectedHR>} />
            <Route path="/hr/reports" element={<ProtectedHR><HRReports /></ProtectedHR>} />
            <Route path="/hr/export" element={<ProtectedHR><HRExport /></ProtectedHR>} />
            <Route path="/hr/trash" element={<ProtectedHR><HRTrash /></ProtectedHR>} />
            <Route path="/resultaten" element={<ProtectedPlain><Resultaten /></ProtectedPlain>} />
            <Route path="/kassa" element={<KassaApp />} />
            <Route path="/kassa/klant" element={<CustomerDisplay />} />
            <Route path="/kassa/klant/:shop" element={<CustomerDisplay />} />
            <Route path="/kassa/:shop" element={<KassaApp />} />
            {singhRoutes(singhAfmelden)}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
